import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  BookOpen, Search, Globe, ChevronRight, Clock, Eye,
  GraduationCap, Filter, X, ChevronDown, ChevronUp, Bell,
  BellOff, CheckCircle, AlertCircle, Calendar, Award,
  Lightbulb, RotateCcw, Lock, Play, Volume2, Map, Pencil, Wand2, Save, Loader2
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { getCountryCode } from '../utils/languageUtils';
import { useWordPopover } from '../shared/reader/useWordPopover';
import SharedWordPopover from '../shared/reader/WordPopover';

const GrammarLibrary = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  const { user } = useAuth();
  
  const [languages, setLanguages] = useState([]);
  const [topics, setTopics] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [reviewRecommendations, setReviewRecommendations] = useState([]);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  
  // Word popover for grammar examples
  const grammarLangCode = selectedTopic?.language_code || searchParams.get('language') || searchParams.get('lang') || '';
  const popover = useWordPopover({ languageCode: grammarLangCode });
  
  // Exercise state
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [exerciseAnswers, setExerciseAnswers] = useState({});
  const [exerciseResults, setExerciseResults] = useState({});
  
  // Filters
  const [selectedLanguage, setSelectedLanguage] = useState(searchParams.get('lang') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Inline edit state
  const [editTopicId, setEditTopicId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editEnriching, setEditEnriching] = useState(false);

  const languageInfo = {
    // 20 Spearhead Languages
    en: { name: 'English', flag: 'gb' },
    es: { name: 'Spanish', flag: 'es' },
    pt: { name: 'Portuguese', flag: 'br' },
    fr: { name: 'French', flag: 'fr' },
    de: { name: 'German', flag: 'de' },
    it: { name: 'Italian', flag: 'it' },
    ru: { name: 'Russian', flag: 'ru' },
    zh: { name: 'Chinese', flag: 'cn' },
    ar: { name: 'Arabic', flag: 'sa' },
    hi: { name: 'Hindi', flag: 'in' },
    ja: { name: 'Japanese', flag: 'jp' },
    ko: { name: 'Korean', flag: 'kr' },
    tr: { name: 'Turkish', flag: 'tr' },
    id: { name: 'Indonesian', flag: 'id' },
    vi: { name: 'Vietnamese', flag: 'vn' },
    th: { name: 'Thai', flag: 'th' },
    sw: { name: 'Swahili', flag: 'ke' },
    fa: { name: 'Persian', flag: 'ir' },
    he: { name: 'Hebrew', flag: 'il' },
    ps: { name: 'Pashto', flag: 'af' },
    // Biblical languages
    hbo: { name: 'Biblical Hebrew', flag: 'il' },
    grc: { name: 'Biblical Greek', flag: 'gr' },
    // Other common
    no: { name: 'Norwegian', flag: 'no' },
    pl: { name: 'Polish', flag: 'pl' },
    uk: { name: 'Ukrainian', flag: 'ua' },
    nl: { name: 'Dutch', flag: 'nl' },
    sv: { name: 'Swedish', flag: 'se' },
    da: { name: 'Danish', flag: 'dk' },
    fi: { name: 'Finnish', flag: 'fi' },
    el: { name: 'Greek', flag: 'gr' },
    cs: { name: 'Czech', flag: 'cz' },
    hu: { name: 'Hungarian', flag: 'hu' },
    ro: { name: 'Romanian', flag: 'ro' },
    multi: { name: 'Multilingual', flag: 'un' },
  };

  const difficultyColors = {
    beginner: 'bg-green-500/20 text-green-400 border-green-500/30',
    elementary: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    intermediate: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    upper_intermediate: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    advanced: 'bg-red-500/20 text-red-400 border-red-500/30',
    proficient: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  const strengthColors = {
    fresh: { bg: 'bg-green-500', text: 'text-green-400' },
    good: { bg: 'bg-yellow-500', text: 'text-yellow-400' },
    fading: { bg: 'bg-orange-500', text: 'text-orange-400' },
    forgotten: { bg: 'bg-red-500', text: 'text-red-400' },
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [selectedLanguage, selectedDifficulty, page]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [langRes, statsRes] = await Promise.all([
        api.get('/grammar-library/languages'),
        api.get('/grammar-library/stats')
      ]);
      setLanguages(langRes.data.languages);
      setStats(statsRes.data);
      
      // Fetch user progress and review recommendations if logged in
      if (user) {
        try {
          const [progressRes, reviewRes] = await Promise.all([
            api.get('/grammar-library/user-progress'),
            api.get('/grammar-library/review-recommendations?limit=5')
          ]);
          setUserProgress(progressRes.data);
          setReviewRecommendations(reviewRes.data.recommendations || []);
          if (reviewRes.data.recommendations?.length > 0) {
            setShowReviewPanel(true);
          }
        } catch (e) {
          console.log('Could not fetch user progress');
        }
      }
      // Auto-open topic if ?open= query param is present
      const openTopicId = searchParams.get('open');
      if (openTopicId) {
        openTopicDetail(openTopicId);
      }
      // Auto-open the alphabet-overview topic when arriving from an alphabet
      // course via `?alphabet_course=<course_id>`. Previously the header
      // link bounced back to /alphabet-courses, which surfaced as "clicking
      // Overview goes back to the alphabets page instead of the lesson".
      const alphabetCourseId = searchParams.get('alphabet_course');
      if (alphabetCourseId) {
        try {
          const resp = await api.get(`/grammar-library/alphabet-overview/${alphabetCourseId}`);
          const topic = resp.data?.topic;
          if (topic) {
            setSelectedTopic(topic);
            setCurrentExerciseIndex(0);
            setExerciseAnswers({});
            setExerciseResults({});
          }
        } catch (e) {
          console.error('Failed to load alphabet overview', e);
        }
      }
    } catch (err) {
      console.error('Failed to load grammar library:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedLanguage) params.append('language_code', selectedLanguage);
      if (selectedDifficulty) params.append('difficulty', selectedDifficulty);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', page.toString());
      params.append('limit', '20');
      
      const response = await api.get(`/grammar-library/topics?${params}`);
      setTopics(response.data.topics);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      console.error('Failed to load topics:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTopics();
  };

  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode === selectedLanguage ? '' : langCode);
    setPage(1);
    if (langCode) {
      setSearchParams({ lang: langCode });
    } else {
      setSearchParams({});
    }
  };

  const openTopicDetail = async (topicId) => {
    try {
      const response = await api.get(`/grammar-library/topic/${topicId}`);
      setSelectedTopic(response.data.topic);
      setCurrentExerciseIndex(0);
      setExerciseAnswers({});
      setExerciseResults({});
      // Refresh topics to update visit info
      fetchTopics();
    } catch (err) {
      console.error('Failed to load topic:', err);
    }
  };

  const dismissReminder = async (topicId) => {
    try {
      await api.post(`/grammar-library/topic/${topicId}/dismiss-reminder`);
      setReviewRecommendations(prev => prev.filter(r => r.topic_id !== topicId));
    } catch (err) {
      console.error('Failed to dismiss reminder:', err);
    }
  };

  const checkExerciseAnswer = async (exerciseIndex, answer) => {
    if (!selectedTopic) return;
    
    try {
      const response = await api.post(
        `/grammar-library/exercise/${selectedTopic.id}/check?exercise_index=${exerciseIndex}&answer=${encodeURIComponent(answer)}`
      );
      setExerciseResults(prev => ({
        ...prev,
        [exerciseIndex]: response.data
      }));
    } catch (err) {
      console.error('Failed to check answer:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const startEditTopic = async (topicId, e) => {
    e.stopPropagation();
    try {
      const resp = await api.get(`/grammar-library/topic/${topicId}`);
      const topic = resp.data;
      setEditTopicId(topicId);
      setEditTitle(topic.title || '');
      setEditContent(topic.content || '');
    } catch { toast.error('Failed to load topic for editing'); }
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      await api.put(`/grammar-library/${editTopicId}`, { title: editTitle, content: editContent });
      toast.success('Topic saved');
      setEditTopicId(null);
      fetchTopics();
    } catch { toast.error('Failed to save'); }
    finally { setEditSaving(false); }
  };

  const handleAIEnrich = async () => {
    setEditEnriching(true);
    try {
      const resp = await api.post(`/grammar-library/topics/${editTopicId}/enrich`);
      if (resp.data?.content) setEditContent(resp.data.content);
      if (resp.data?.title) setEditTitle(resp.data.title);
      toast.success('AI enrichment applied — review and save');
    } catch { toast.error('AI enrichment failed'); }
    finally { setEditEnriching(false); }
  };

  const FlagImage = ({ langCode, size = 24 }) => {
    const info = languageInfo[langCode];
    const countryCode = info?.flag || getCountryCode(langCode) || 'un';
    return (
      <img 
        src={`https://flagcdn.com/w40/${countryCode}.png`}
        alt={info?.name || langCode}
        className="rounded-sm object-cover"
        style={{ width: size, height: size * 0.75 }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  };

  const MemoryStrengthBar = ({ strength, level }) => {
    const colors = strengthColors[level] || strengthColors.forgotten;
    return (
      <div className="flex items-center gap-2">
        <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={`h-full ${colors.bg} transition-all duration-300`}
            style={{ width: `${strength * 100}%` }}
          />
        </div>
        <span className={`text-xs ${colors.text}`}>{Math.round(strength * 100)}%</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="w-10 h-10 text-amber-500" />
            <h1 className="text-4xl font-bold text-white">Grammar Library</h1>
          </div>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Explore grammar topics across {languages.length} languages with {stats?.total_topics || 0} lessons
          </p>
        </div>

        {/* Review Reminders Panel */}
        {showReviewPanel && reviewRecommendations.length > 0 && (
          <div className="mb-8 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-orange-400" />
                <h3 className="text-white font-semibold">Review Reminder</h3>
                <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">
                  Based on Ebbinghaus Forgetting Curve
                </span>
              </div>
              <button 
                onClick={() => setShowReviewPanel(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Research shows that reviewing at optimal intervals significantly strengthens long-term memory retention. 
              These topics are fading from memory:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {reviewRecommendations.map(rec => (
                <div 
                  key={rec.topic_id}
                  className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 hover:border-orange-500/50 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FlagImage langCode={rec.language_code} size={20} />
                      <span className="text-white font-medium text-sm">{rec.title}</span>
                    </div>
                    <button 
                      onClick={() => dismissReminder(rec.topic_id)}
                      className="text-slate-500 hover:text-slate-300 p-1"
                      title="Don't show this reminder again"
                    >
                      <BellOff className="w-4 h-4" />
                    </button>
                  </div>
                  <MemoryStrengthBar strength={rec.memory_strength} level={rec.review_status?.level} />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">
                      {rec.days_since_review} days ago
                    </span>
                    <button 
                      onClick={() => openTopicDetail(rec.topic_id)}
                      className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Review Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Progress Summary */}
        {userProgress && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-500">{userProgress.topics_visited}</div>
              <div className="text-slate-400 text-sm">Topics Visited</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-blue-500">{userProgress.total_topics}</div>
              <div className="text-slate-400 text-sm">Total Topics</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-green-500">{userProgress.completion_percentage}%</div>
              <div className="text-slate-400 text-sm">Completion</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-orange-500">{userProgress.topics_needing_review}</div>
              <div className="text-slate-400 text-sm">Need Review</div>
            </div>
          </div>
        )}

        {/* Stats Cards (for non-logged in users) */}
        {!userProgress && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-500">{stats?.total_topics || 0}</div>
              <div className="text-slate-400 text-sm">Total Topics</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-blue-500">{languages.length}</div>
              <div className="text-slate-400 text-sm">Languages</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-green-500">{stats?.topics_with_exercises || 0}</div>
              <div className="text-slate-400 text-sm">With Exercises</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-purple-500">{stats?.by_difficulty?.intermediate || 0}</div>
              <div className="text-slate-400 text-sm">Intermediate+</div>
            </div>
          </div>
        )}

        {/* Language Filter Chips */}
        <div className="mb-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Globe className="w-5 h-5 text-amber-500" />
            Filter by Language
          </h3>
          <div className="flex flex-wrap gap-2">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  selectedLanguage === lang.code
                    ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                    : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
              >
                <FlagImage langCode={lang.code} size={20} />
                <span>{languageInfo[lang.code]?.name || lang.name}</span>
                <span className="text-xs bg-slate-700/50 px-1.5 py-0.5 rounded">{lang.topic_count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search grammar topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </form>
          
          <select
            value={selectedDifficulty}
            onChange={(e) => { setSelectedDifficulty(e.target.value); setPage(1); }}
            className="px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50"
          >
            <option value="">All Levels</option>
            <option value="beginner">Beginner (A1)</option>
            <option value="elementary">Elementary (A2)</option>
            <option value="intermediate">Intermediate (B1-B2)</option>
            <option value="advanced">Advanced (C1-C2)</option>
          </select>
          
          {/* Learning Path Button */}
          {selectedLanguage && (
            <button
              onClick={() => navigate(`/learning-path/${selectedLanguage}`)}
              className="px-4 py-3 bg-purple-500/20 border border-purple-500/50 rounded-xl text-purple-400 hover:bg-purple-500/30 transition-colors flex items-center gap-2"
            >
              <Map className="w-5 h-5" />
              <span className="hidden md:inline">Learning Path</span>
            </button>
          )}
        </div>

        {/* Topics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {topics.map(topic => (
            <div
              key={topic.id}
              onClick={() => openTopicDetail(topic.id)}
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 hover:border-amber-500/50 transition-all cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FlagImage langCode={topic.language_code} size={24} />
                  <span className="text-slate-400 text-sm uppercase">
                    {languageInfo[topic.language_code]?.name || topic.language_code}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {topic.is_free_lesson && (
                    <span
                      className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded border border-green-500/40 font-semibold"
                      data-testid={`free-lesson-badge-${topic.id}`}
                    >
                      Free
                    </span>
                  )}
                  {topic.has_exercises && (
                    <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded border border-purple-500/30">
                      <GraduationCap className="w-3 h-3 inline mr-1" />
                      Exercises
                    </span>
                  )}
                  {topic.cefr_level && (
                    <span className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-300">
                      {topic.cefr_level}
                    </span>
                  )}
                </div>
              </div>
              
              <h3 className="text-white font-semibold mb-2 group-hover:text-amber-400 transition-colors">
                {topic.title || (topic.objectives?.[0]?.slice(0, 60) + (topic.objectives?.[0]?.length > 60 ? '...' : '')) || `${topic.cefr_level || ''} Topic`}
              </h3>
              
              {topic.title_native && (
                <p className="text-slate-500 text-sm mb-2">{topic.title_native}</p>
              )}
              
              {/* Visit info */}
              {topic.last_visited && (
                <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
                  <Eye className="w-3 h-3" />
                  <span>Visited on {formatDate(topic.last_visited)}</span>
                  {topic.memory_strength !== undefined && (
                    <MemoryStrengthBar 
                      strength={topic.memory_strength} 
                      level={topic.review_status?.level} 
                    />
                  )}
                </div>
              )}
              
              <div className="flex items-center gap-3 text-sm text-slate-400">
                {topic.difficulty && (
                  <span className={`px-2 py-0.5 rounded border text-xs ${difficultyColors[topic.difficulty] || 'bg-slate-700/50 text-slate-400 border-slate-600'}`}>
                    {topic.difficulty}
                  </span>
                )}
                {topic.study_time_minutes && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {topic.study_time_minutes}m
                  </span>
                )}
              </div>
              
              <div className="flex items-center justify-between mt-4">
                {user?.is_admin && (
                  <button
                    onClick={(e) => startEditTopic(topic.id, e)}
                    className="p-1.5 rounded hover:bg-slate-700 text-slate-500 hover:text-amber-400 transition-colors"
                    data-testid={`edit-topic-${topic.id}`}
                    title="Edit topic"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-amber-500 transition-colors ml-auto" />
              </div>
              
              {/* Inline editor */}
              {editTopicId === topic.id && (
                <div className="mt-3 pt-3 border-t border-slate-700 space-y-2" onClick={e => e.stopPropagation()}>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    className="w-full px-3 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm"
                    placeholder="Title"
                  />
                  <textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-sm min-h-[120px] max-h-[300px] resize-y"
                    placeholder="Content (markdown supported)"
                  />
                  <div className="flex items-center gap-2">
                    <button onClick={handleAIEnrich} disabled={editEnriching} className="flex items-center gap-1 px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs disabled:opacity-50">
                      {editEnriching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} AI Enrich
                    </button>
                    <button onClick={handleEditSave} disabled={editSaving} className="flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs disabled:opacity-50">
                      {editSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
                    </button>
                    <button onClick={() => setEditTopicId(null)} className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white text-xs">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {topics.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No topics found</h3>
            <p className="text-slate-400">Try adjusting your filters or search query</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-slate-400">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        {/* Topic Detail Modal */}
        {selectedTopic && (
          <TopicDetailModal
            topic={selectedTopic}
            user={user}
            languageInfo={languageInfo}
            difficultyColors={difficultyColors}
            strengthColors={strengthColors}
            formatDate={formatDate}
            FlagImage={FlagImage}
            MemoryStrengthBar={MemoryStrengthBar}
            currentExerciseIndex={currentExerciseIndex}
            setCurrentExerciseIndex={setCurrentExerciseIndex}
            exerciseAnswers={exerciseAnswers}
            setExerciseAnswers={setExerciseAnswers}
            exerciseResults={exerciseResults}
            checkExerciseAnswer={checkExerciseAnswer}
            onClose={() => setSelectedTopic(null)}
            navigate={navigate}
            stats={stats}
            onWordClick={(term) => popover.openForTerm(term)}
          />
        )}
      </div>

      {/* Shared Word Popover for grammar examples */}
      {popover.selectedToken && popover.popoverPos && (
        <SharedWordPopover
          style={popover.popoverPos}
          wordData={popover.wordData}
          wordLoading={popover.wordLoading}
          language={{ code: grammarLangCode }}
          token={popover.selectedToken}
          onClose={() => popover.close()}
          onSetStatus={() => popover.close()}
          locked={popover.wordData?.locked}
          lockedFields={popover.wordData?.locked_fields || []}
          onRelatedWordClick={(term) => popover.openForTerm(term)}
          isNewEntry={popover.isNewEntry}
        />
      )}
    </div>
  );
};

// Topic Detail Modal Component
const TopicDetailModal = ({
  topic,
  user,
  languageInfo,
  difficultyColors,
  strengthColors,
  formatDate,
  FlagImage,
  MemoryStrengthBar,
  currentExerciseIndex,
  setCurrentExerciseIndex,
  exerciseAnswers,
  setExerciseAnswers,
  exerciseResults,
  checkExerciseAnswer,
  onClose,
  navigate,
  stats,
  onWordClick
}) => {
  const [showExercises, setShowExercises] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (idx) => {
    setExpandedSections(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const userTier = user?.tier || 1;
  const hasExercises = topic.exercises && topic.exercises.length > 0;
  const isFreeLesson = topic.is_free_lesson === true;
  const canAccessExercises = userTier >= 2 || isFreeLesson || user?.is_admin;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-6 z-10">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <FlagImage langCode={topic.language_code} size={32} />
              <div>
                <h2 className="text-2xl font-bold text-white">{topic.title || (topic.objectives?.[0]?.slice(0, 80)) || `${topic.cefr_level || ''} Grammar Topic`}</h2>
                {topic.title_native && (
                  <p className="text-slate-400">{topic.title_native}</p>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg">
              <X className="w-6 h-6 text-slate-400" />
            </button>
          </div>
          
          {/* Visit Info */}
          {topic.last_visited && (
            <div className="mt-4 flex items-center gap-4 p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-300">
                  Visited on {formatDate(topic.last_visited)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-300">{topic.visit_count} visits</span>
              </div>
              {topic.memory_strength !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">Memory:</span>
                  <MemoryStrengthBar 
                    strength={topic.memory_strength} 
                    level={topic.review_status?.level} 
                  />
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Meta Info */}
          <div className="flex flex-wrap gap-3">
            {topic.is_free_lesson && (
              <span
                className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm border border-green-500/30 font-semibold"
                data-testid="grammar-free-lesson-pill"
              >
                Free Lesson
              </span>
            )}
            {topic.cefr_level && (
              <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-sm">
                CEFR {topic.cefr_level}
              </span>
            )}
            {topic.difficulty && (
              <span className={`px-3 py-1 rounded-lg text-sm border ${difficultyColors[topic.difficulty]}`}>
                {topic.difficulty}
              </span>
            )}
            {topic.study_time_minutes && (
              <span className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-lg text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {topic.study_time_minutes} minutes
              </span>
            )}
          </div>
          
          {/* Objectives */}
          {topic.objectives && topic.objectives.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-amber-500" />
                Learning Objectives
              </h3>
              <ul className="space-y-2">
                {topic.objectives.map((obj, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-300">
                    <ChevronRight className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    {obj}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Content Sections */}
          {topic.content_sections && topic.content_sections.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Lesson Content</h3>
              {topic.content_sections.map((section, idx) => (
                <div key={idx} className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection(idx)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/50"
                  >
                    <h4 className="text-white font-medium">{section.title}</h4>
                    {expandedSections[idx] !== false ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                  {expandedSections[idx] !== false && (
                    <div className="px-4 pb-4">
                      <div className="text-slate-300 whitespace-pre-wrap text-sm leading-relaxed">
                        {section.content}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Examples */}
          {topic.examples && topic.examples.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Examples</h3>
              <div className="bg-slate-800/50 rounded-xl p-4 space-y-2">
                {topic.examples.map((ex, idx) => (
                  <p key={idx} className="text-slate-300 font-mono text-sm">
                    {ex.split(/(\s+)/).map((part, pi) => {
                      const cleaned = part.replace(/[.,!?;:"""''«»¿¡—\-()[\]{}]/g, '').trim();
                      if (!cleaned || /^\s+$/.test(part)) return <span key={pi}>{part}</span>;
                      return (
                        <span
                          key={pi}
                          className="cursor-pointer hover:text-amber-400 hover:underline transition-colors"
                          onClick={() => onWordClick?.(cleaned)}
                          data-testid={`grammar-word-${idx}-${pi}`}
                        >{part}</span>
                      );
                    })}
                  </p>
                ))}
              </div>
            </div>
          )}
          
          {/* Tables */}
          {topic.tables && topic.tables.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Reference Tables</h3>
              {topic.tables.map((table, idx) => (
                <div key={idx} className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <tbody>
                      {table.map((row, rowIdx) => (
                        <tr key={rowIdx} className={rowIdx === 0 ? 'bg-slate-700/50' : 'bg-slate-800/30'}>
                          {row.map((cell, cellIdx) => (
                            <td 
                              key={cellIdx} 
                              className={`px-3 py-2 border border-slate-700 text-sm ${
                                rowIdx === 0 ? 'text-white font-semibold' : 'text-slate-300'
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
          
          {/* Exercises Section */}
          <div className="border-t border-slate-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-500" />
                Practice Exercises
              </h3>
              {hasExercises && canAccessExercises && (
                <button
                  onClick={() => setShowExercises(!showExercises)}
                  className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  {showExercises ? 'Hide Exercises' : 'Start Exercises'}
                </button>
              )}
            </div>
            
            {!canAccessExercises && (
              <div className="bg-gradient-to-br from-amber-500/10 to-purple-500/10 border border-amber-500/30 rounded-xl p-6 text-center" data-testid="grammar-exercise-paywall">
                <Lock className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h4 className="text-white font-semibold mb-2">Unlock Practice Exercises</h4>
                <p className="text-slate-300 text-sm mb-4">
                  This is a paid lesson. Your <span className="text-amber-400 font-semibold">first lesson</span> in
                  every language is free — exercises and feedback included. Upgrade to Tier 2+ to unlock interactive
                  exercises across all <span className="font-semibold">{stats?.total_topics || 600}+</span> grammar lessons.
                </p>
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-5 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
                  data-testid="grammar-paywall-upgrade-btn"
                >
                  Upgrade Account
                </button>
              </div>
            )}
            
            {canAccessExercises && !hasExercises && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
                <Lightbulb className="w-12 h-12 text-yellow-500/50 mx-auto mb-3" />
                <p className="text-slate-400">
                  Exercises for this topic are being generated. Check back soon!
                </p>
              </div>
            )}
            
            {canAccessExercises && hasExercises && showExercises && (
              <ExercisePanel
                exercises={topic.exercises}
                currentIndex={currentExerciseIndex}
                setCurrentIndex={setCurrentExerciseIndex}
                answers={exerciseAnswers}
                setAnswers={setExerciseAnswers}
                results={exerciseResults}
                checkAnswer={checkExerciseAnswer}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Exercise Panel Component
const ExercisePanel = ({
  exercises,
  currentIndex,
  setCurrentIndex,
  answers,
  setAnswers,
  results,
  checkAnswer
}) => {
  const exercise = exercises[currentIndex];
  const result = results[currentIndex];
  const currentAnswer = answers[currentIndex] || '';

  const handleSubmit = () => {
    if (currentAnswer.trim()) {
      checkAnswer(currentIndex, currentAnswer);
    }
  };

  const handleOptionSelect = (option) => {
    setAnswers(prev => ({ ...prev, [currentIndex]: option }));
    checkAnswer(currentIndex, option);
  };

  return (
    <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
      {/* Progress */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-slate-400 text-sm">
          Exercise {currentIndex + 1} of {exercises.length}
        </span>
        <div className="flex gap-1">
          {exercises.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                idx === currentIndex
                  ? 'bg-amber-500 text-white'
                  : results[idx]
                    ? results[idx].correct
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      </div>
      
      {/* Question */}
      <div className="mb-6">
        <span className="text-xs text-purple-400 uppercase tracking-wider">{exercise.type}</span>
        <p className="text-white text-lg mt-2">{exercise.question}</p>
      </div>
      
      {/* Answer Input */}
      {exercise.options ? (
        <div className="grid grid-cols-2 gap-3 mb-6">
          {exercise.options.map((option, idx) => (
            <button
              key={idx}
              onClick={() => handleOptionSelect(option)}
              disabled={result !== undefined}
              className={`p-3 rounded-lg border text-left transition-colors ${
                result
                  ? option === result.correct_answer
                    ? 'bg-green-500/20 border-green-500 text-green-400'
                    : option === result.user_answer && !result.correct
                      ? 'bg-red-500/20 border-red-500 text-red-400'
                      : 'bg-slate-800/50 border-slate-700 text-slate-400'
                  : currentAnswer === option
                    ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                    : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      ) : (
        <div className="mb-6">
          <input
            type="text"
            value={currentAnswer}
            onChange={(e) => setAnswers(prev => ({ ...prev, [currentIndex]: e.target.value }))}
            placeholder="Type your answer..."
            disabled={result !== undefined}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 disabled:opacity-50"
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          />
          {!result && (
            <button
              onClick={handleSubmit}
              disabled={!currentAnswer.trim()}
              className="mt-3 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Check Answer
            </button>
          )}
        </div>
      )}
      
      {/* Result */}
      {result && (
        <div className={`p-4 rounded-lg ${result.correct ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
          <div className="flex items-center gap-2 mb-2">
            {result.correct ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-semibold">Correct!</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-semibold">Incorrect</span>
              </>
            )}
          </div>
          {!result.correct && (
            <p className="text-slate-300 text-sm mb-2">
              Correct answer: <span className="text-amber-400 font-medium">{result.correct_answer}</span>
            </p>
          )}
          {result.explanation && (
            <p className="text-slate-400 text-sm">{result.explanation}</p>
          )}
        </div>
      )}
      
      {/* Navigation */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
        <button
          onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
          disabled={currentIndex === 0}
          className="px-4 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => setCurrentIndex(i => Math.min(exercises.length - 1, i + 1))}
          disabled={currentIndex === exercises.length - 1}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default GrammarLibrary;
